import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactModal from "../components/ContactModal";
import SEOHead from "../components/SEOHead";
// Removed config import to avoid production issues
import AISearchOptimization from "../components/AISearchOptimization";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Calendar, 
  CheckCircle, 
  ArrowRight, 
  Zap, 
  Users, 
  Target, 
  TrendingUp, 
  Shield,
  Clock,
  Award,
  Lightbulb,
  Settings,
  BarChart3
} from "lucide-react";

const KITransformation = () => {
  const { t } = useLanguage();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // SEO Configuration
  const seoConfig = {
    title: "Ganzheitliche KI-Transformation für Unternehmen | Nachhaltige AI-Strategien | BROVKO",
    description: "Ganzheitliche KI-Transformation für nachhaltigen Unternehmenserfolg. Strategische AI-Roadmaps, Change Management und Enterprise KI-Lösungen. Jetzt Transformation starten!",
    keywords: "Ganzheitliche KI-Transformation, Nachhaltige KI-Transformation, KI-Roadmap für Unternehmen, Change Management KI, Enterprise AI-Strategien, Strategische KI-Beratung, AI Transformation Consulting",
    canonicalUrl: "/leistungsangebot/ki-transformation",
    ogTitle: "Ganzheitliche KI-Transformation | Nachhaltige AI-Strategien für Unternehmen",
    ogDescription: "Führende KI-Transformation für nachhaltigen Erfolg. Strategische AI-Roadmaps, Change Management und Enterprise KI-Lösungen für Ihr Unternehmen.",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Ganzheitliche KI-Transformation",
      "description": "Nachhaltige KI-Transformation für Unternehmen mit strategischen AI-Roadmaps und Change Management",
      "provider": {
        "@type": "Organization",
        "name": "BROVKO"
      },
      "areaServed": ["DE", "AT", "CH"],
      "serviceType": "KI-Transformation Consulting",
      "offers": {
        "@type": "Offer",
        "description": "Ganzheitliche KI-Transformation Services",
        "areaServed": ["Germany", "Austria", "Switzerland"]
      }
    }
  };

  const openContactModal = (buttonContext = null, buttonText = null) => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const transformationPhases = [
    {
      icon: Target,
      title: "Phase 1: Strategische Ausrichtung",
      subtitle: "Fundament für erfolgreiche KI-Transformation",
      features: [
        "KI-Readiness Assessment und Reifegradbestimmung",
        "Entwicklung einer maßgeschneiderten KI-Transformationsstrategie",
        "Definition von Zielen, KPIs und Erfolgskriterien",
        "Stakeholder-Alignment und Executive Buy-in"
      ],
      duration: "4-6 Wochen"
    },
    {
      icon: Users,
      title: "Phase 2: Organisationsentwicklung",
      subtitle: "Menschen und Kultur für KI befähigen",
      features: [
        "Change Management und Kommunikationsstrategie",
        "KI-Schulungen und Kompetenzaufbau",
        "Aufbau von KI-Teams und Governance-Strukturen",
        "Etablierung einer datengetriebenen Unternehmenskultur"
      ],
      duration: "6-8 Wochen"
    },
    {
      icon: Settings,
      title: "Phase 3: Technische Implementierung",
      subtitle: "KI-Lösungen systematisch einführen",
      features: [
        "Aufbau der technischen KI-Infrastruktur",
        "Implementierung von Pilotprojekten und Use Cases",
        "Datenqualität und -governance optimieren",
        "Integration in bestehende Systeme und Prozesse"
      ],
      duration: "8-12 Wochen"
    },
    {
      icon: TrendingUp,
      title: "Phase 4: Skalierung & Optimierung",
      subtitle: "Nachhaltige Transformation sicherstellen",
      features: [
        "Skalierung erfolgreicher KI-Lösungen",
        "Kontinuierliche Überwachung und Optimierung",
        "Aufbau interner KI-Expertise und Autonomie",
        "Messung und Reporting von Transformationserfolg"
      ],
      duration: "Kontinuierlich"
    }
  ];

  const transformationServices = [
    {
      icon: Lightbulb,
      title: "KI-Strategie & Roadmap",
      description: "Entwicklung einer ganzheitlichen KI-Transformationsstrategie mit klarer Roadmap und Priorisierung von Use Cases.",
      benefits: [
        "Strategische KI-Vision und -Ziele",
        "Priorisierte Use-Case-Pipeline",
        "ROI-basierte Investitionsplanung",
        "Risikomanagement und Compliance"
      ]
    },
    {
      icon: Users,
      title: "Change Management & Kulturwandel",
      description: "Begleitung des kulturellen Wandels und Befähigung der Mitarbeitenden für die KI-getriebene Zukunft.",
      benefits: [
        "Strukturierte Change-Prozesse",
        "KI-Kompetenzaufbau im Unternehmen",
        "Widerstandsmanagement",
        "Aufbau einer Innovationskultur"
      ]
    },
    {
      icon: Settings,
      title: "Technologie-Integration",
      description: "Systematische Einführung und Integration von KI-Technologien in bestehende Unternehmensarchitekturen.",
      benefits: [
        "Technische KI-Infrastruktur",
        "System- und Datenintegration",
        "Skalierbare KI-Plattformen",
        "Sicherheit und Compliance"
      ]
    },
    {
      icon: BarChart3,
      title: "Performance Monitoring",
      description: "Kontinuierliche Überwachung und Optimierung der KI-Transformation mit messbaren KPIs und Erfolgsmetriken.",
      benefits: [
        "KI-Erfolg messbar machen",
        "Kontinuierliche Optimierung",
        "Business Impact Tracking",
        "Transformation Dashboard"
      ]
    }
  ];

  const industryExamples = [
    {
      industry: "Produktion & Manufacturing",
      transformation: "Predictive Maintenance, Qualitätskontrolle, Supply Chain Optimierung",
      impact: "25% weniger Ausfallzeiten, 15% Kosteneinsparung"
    },
    {
      industry: "Finanzdienstleistungen",
      transformation: "Automated Risk Assessment, Fraud Detection, Personalized Banking",
      impact: "40% schnellere Kreditentscheidungen, 60% weniger Betrug"
    },
    {
      industry: "Einzelhandel & E-Commerce",
      transformation: "Personalisierung, Demand Forecasting, Chatbots, Dynamic Pricing",
      impact: "20% höhere Conversion, 30% bessere Lagerhaltung"
    },
    {
      industry: "Healthcare & Pharma",
      transformation: "Diagnostik-Unterstützung, Drug Discovery, Patient Care Optimization",
      impact: "50% schnellere Diagnosen, 25% effizientere Behandlung"
    }
  ];

  const successFactors = [
    "Klare Vision und Commitment der Geschäftsführung",
    "Schrittweise Einführung mit schnellen Erfolgen (Quick Wins)",
    "Starker Fokus auf Datenqualität und -governance",
    "Kontinuierlicher Kompetenzaufbau der Mitarbeitenden",
    "Agile Vorgehensweise mit iterativen Verbesserungen",
    "Messbare KPIs und regelmäßige Erfolgskontrolle"
  ];

  const whyBrovko = [
    "Ganzheitlicher Ansatz – von Strategie bis Umsetzung",
    "Branchenübergreifende Expertise aus 100+ erfolgreichen Projekten",
    "Bewährte Methodik mit KI-gestützten Tools und Frameworks",
    "Fokus auf nachhaltigen Wandel und interne Befähigung",
    "Messbarer ROI – oft bereits in den ersten 90 Tagen"
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <SEOHead {...seoConfig} />
      <Header />
      
      {/* Hero Section - COMPACT */}
      <section className="pt-40 pb-10">
        <div className="container mx-auto px-4">
          <div className="max-w-[46rem]">
            <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-4 leading-tight">
              {t('kiTransformation.hero.title')}
            </h1>
            <p className="text-base text-ink-muted mb-4 leading-relaxed">
              {t('kiTransformation.hero.description')}
            </p>
            <p className="text-sm text-ink-faint mb-6">
              {t('kiTransformation.hero.subtitle')}
            </p>
            <Button 
              className="bg-teal-400 hover:bg-brand text-ink-onBrand px-6 py-3 text-sm font-semibold"
              onClick={() => openContactModal("Hero Section", t('kiTransformation.hero.cta'))}
            >
              <Calendar className="mr-2 h-4 w-4" />
              {t('kiTransformation.hero.cta')}
            </Button>
          </div>
        </div>
      </section>

      {/* Why Brovko Section - COMPACT */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-8 text-center">
              {t('kiTransformation.whyBrovko.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {whyBrovko.map((item, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-brand-400 mt-1 flex-shrink-0" />
                  <p className="text-ink-muted text-sm leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Phases - COMPACT */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-8 text-center">
              {t('kiTransformation.phases.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {transformationPhases.map((phase, index) => {
                const IconComponent = phase.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-teal-400/10 p-2 rounded-lg">
                        <IconComponent className="h-5 w-5 text-brand-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base font-bold text-white">{phase.title}</h3>
                        <p className="text-ink-faint text-xs mt-0.5">{phase.subtitle}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Clock className="h-3 w-3 text-brand-400" />
                          <span className="text-brand-400 text-xs font-medium">{phase.duration}</span>
                        </div>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {phase.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-3 w-3 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-xs leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Services - COMPACT */}
      <section className="py-20 md:py-28 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-8 text-center">
              {t('kiTransformation.services.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {transformationServices.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-5 border border-hairline">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-teal-400/10 p-2 rounded-lg">
                        <IconComponent className="h-5 w-5 text-brand-400" />
                      </div>
                      <h3 className="text-base font-bold text-white">{service.title}</h3>
                    </div>
                    <p className="text-ink-muted text-sm mb-4 leading-relaxed">{service.description}</p>
                    <ul className="grid grid-cols-2 gap-1.5">
                      {service.benefits.map((benefit, bIndex) => (
                        <li key={bIndex} className="flex items-start gap-1.5">
                          <CheckCircle className="h-3 w-3 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-xs leading-relaxed">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Industry Examples - COMPACT */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-6 text-center">
              {t('kiTransformation.industry.title')}
            </h2>
            <p className="text-base text-ink-faint mb-8 text-center">
              {t('kiTransformation.industry.subtitle')}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {industryExamples.map((example, index) => (
                <div key={index} className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
                  <h3 className="text-sm font-semibold text-white mb-2">{example.industry}</h3>
                  <p className="text-ink-muted text-xs mb-3 leading-relaxed">{example.transformation}</p>
                  <div className="flex items-center gap-1.5">
                    <Award className="h-3 w-3 text-brand-400" />
                    <span className="text-brand-400 text-xs font-medium">{example.impact}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Success Factors - COMPACT */}
      <section className="py-20 md:py-28 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-4 text-center">
              {t('kiTransformation.success.title')}
            </h2>
            <p className="text-base text-ink-faint mb-6 text-center">
              {t('kiTransformation.success.subtitle')}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {successFactors.map((factor, index) => (
                <div key={index} className="flex items-start gap-2 bg-slate-800/30 rounded-lg p-3">
                  <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                  <p className="text-ink-muted text-xs leading-relaxed">{factor}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - COMPACT */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-4">
              {t('kiTransformation.cta.title')}
            </h2>
            <p className="text-base text-ink-muted mb-6">
              {t('kiTransformation.cta.description')}
            </p>
            <div className="flex justify-center">
              <Button 
                className="bg-teal-400 hover:bg-brand text-ink-onBrand px-6 py-3 text-sm font-semibold"
                onClick={() => openContactModal("CTA Section", t('kiTransformation.hero.cta'))}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {t('kiTransformation.hero.cta')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="KI-Transformation"
        buttonContext="KI-Transformation Service"
        buttonText="Transformation planen"
      />
      
      {/* AI Search Optimization für KI-Transformation */}
      <AISearchOptimization 
        pageType="service"
        title={t('kiTransformation.hero.title')}
        description="Ganzheitliche KI-Transformation in 4 Phasen: Strategische Ausrichtung, Organisationsentwicklung, Technische Implementierung und Skalierung für nachhaltigen Unternehmenserfolg."
        keywords={[
          'KI-Transformation 4 Phasen',
          'ganzheitliche AI-Transformation',
          'Organisationsentwicklung KI',
          'strategische KI-Ausrichtung',
          'technische AI-Implementierung',
          'KI-Skalierung Unternehmen'
        ]}
        faqData={[
          {
            question: "Was umfasst eine ganzheitliche KI-Transformation?",
            answer: "Eine ganzheitliche KI-Transformation bei BROVKO umfasst 4 Phasen: Strategische Ausrichtung (KI-Strategie & Roadmap), Organisationsentwicklung (Change Management & Kulturwandel), Technische Implementierung (Technologie-Integration) und Skalierung & Optimierung (Performance Monitoring)."
          },
          {
            question: "Welche Branchen profitieren am meisten von KI-Transformation?",
            answer: "Alle Branchen profitieren: Produktion & Manufacturing (25% weniger Ausfallzeiten), Finanzdienstleistungen (40% schnellere Kreditentscheidungen), Einzelhandel & E-Commerce (20% höhere Conversion), Healthcare & Pharma (50% schnellere Diagnosen)."
          }
        ]}
      />
    </div>
  );
};

export default KITransformation;